import { useState, useImperativeHandle, forwardRef, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/providers/auth-provider";
import { useTranslation } from "@/providers/language-provider";
import { UserAvatar } from "@/features/user-avatar";
import { ButtonSubmit } from "@/components/blocks/button-submit";
import { ImageUp } from "lucide-react";
import { DialogUpdateUser } from "@/features/dialog-update-user";

import { toast } from "@/hooks/use-toast";

interface DialogProfileHandles {
  open: () => void;
  close: () => void;
}

interface DialogProfileProps {
  children: React.ReactNode;
  user?: any; // Add optional user prop
}

export const DialogProfile = forwardRef<
  DialogProfileHandles,
  DialogProfileProps
>(({ children, user: propUser }, ref) => {
  const { user: authUser, updateImage } = useAuth();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isProfileChanged = selectedImage != null;

  const user = propUser || authUser;
  const canEdit = user === authUser;

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  }));

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelectedImage(null);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const image = reader.result as string;
        setSelectedImage(image);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (selectedImage) {
      const file = fileInputRef.current?.files?.[0];
      if (file) {
        try {
          setIsSubmitting(true);
          // const updatedUserInfo = await apiUploadPicture(file, user.token);
          await updateImage(file);

          toast({
            title: "Upload Successful:",
            description: "Upload profile image is success.",
          });
        } catch (error) {
          console.error("Upload failed:", error);
          toast({
            title: "Upload Failed:",
            description: `${error}`,
          });
        } finally {
          setIsSubmitting(false);
        }
      } else {
        console.error("No file selected");
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center text-2xl">
              {user?.name ?? "Not logged in"}
              {canEdit && <DialogUpdateUser />}
            </div>
          </DialogTitle>
          <DialogDescription>{canEdit && t("ProfileDesc")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="relative flex flex-col justify-center items-center gap-2">
            {/* <div className="relative flex justify-center items-center gap-4"> */}
            <UserAvatar picture={selectedImage || user?.picture} large={true} />
            {canEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute inset-0 m-auto transform"
                // className="absolute inset-0 m-auto transform left-[240px]"
                // className="absolute inset-0 m-auto transform top-[-200px] left-[200px]"
                onClick={handleUploadClick}
              >
                <ImageUp className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        {/* Hidden form to handle file upload */}
        <form style={{ display: "none" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
        </form>
        <DialogFooter>
          <div className="w-full flex justify-between items-center">
            <ButtonSubmit
              variant="destructive"
              className={isProfileChanged ? "" : "invisible"}
              type="submit"
              onClick={handleSave}
              isSubmitting={isSubmitting}
            >
              {t("Save")}
            </ButtonSubmit>
            <Button
              autoFocus
              type="submit"
              onClick={() => handleOpenChange(false)}
            >
              {t("Close")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

DialogProfile.displayName = "DialogProfile";
